// LocationContext — global location state for HCEN Patient website.
//
// YES — a full Location Service is wired in:
//   • src/services/locationService.js  (GPS, IP, geocode, distance)
//   • LocationProvider (this file)     (React state + cache)
//   • LocationChip + CityPickerModal   (manual city pick)
//   • LocationAutoPrompt               (auto “use current location” banner)
//
// Boot order:
//   1. localStorage cache (instant)
//   2. IP fallback if no cache
//   3. AUTO GPS request (browser permission) — for accurate current location
//   4. Manual city pick anytime via location chip
//
// Accuracy: GPS + OpenStreetMap reverse geocode (free).
// Optional: set REACT_APP_GOOGLE_MAPS_API_KEY in .env for Google geocoding.
import React, {
  createContext, useContext, useEffect, useRef, useState, useCallback,
} from 'react';
import { coordsForCity } from '../utils/cityCentroids';
import {
  ipLookup,
  reverseGeocode,
  forwardGeocode,
  getGpsPosition,
  distanceKm,
} from '../services/locationService';

const LS_KEY     = 'hcen_loc_v2';
const LS_RECENTS = 'hcen_loc_recents';
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000;

const LocationContext = createContext(null);

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.detectedAt) return null;
    if (Date.now() - obj.detectedAt > CACHE_MAX_AGE && obj.source !== 'manual') return null;
    return obj;
  } catch { return null; }
}

function writeCache(loc) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(loc)); } catch {}
}

function readRecents() {
  try { return JSON.parse(localStorage.getItem(LS_RECENTS) || '[]'); } catch { return []; }
}

function writeRecents(arr) {
  try { localStorage.setItem(LS_RECENTS, JSON.stringify(arr.slice(0, 5))); } catch {}
}

export function LocationProvider({ children }) {
  const [loc, setLoc]         = useState(() => readCache());
  const [requesting, setReq]  = useState(false);
  const [error, setError]       = useState(null);
  const [permission, setPerm]   = useState('unknown');
  const ipKickedRef             = useRef(false);
  const autoGpsRef              = useRef(false);

  useEffect(() => {
    if (!navigator.permissions?.query) return;
    navigator.permissions.query({ name: 'geolocation' })
      .then(s => { setPerm(s.state); s.onchange = () => setPerm(s.state); })
      .catch(() => {});
  }, []);

  // IP fallback when no cached location
  useEffect(() => {
    if (loc) return;
    if (ipKickedRef.current) return;
    ipKickedRef.current = true;
    ipLookup().then(ipLoc => {
      if (!ipLoc) return;
      setLoc(prev => prev || ipLoc);
      writeCache(ipLoc);
    });
  }, [loc]);

  const applyGpsCoords = useCallback(async (lat, lng, accuracy) => {
    let city = '';
    let area = '';
    const geo = await reverseGeocode(lat, lng);
    if (geo) {
      city = geo.city;
      area = geo.area || '';
    }
    if (!city) {
      city = loc?.city || '';
      area = loc?.area || '';
    }
    const next = {
      coords: { lat, lng },
      city,
      area,
      source: 'gps',
      accuracy: accuracy || 0,
      detectedAt: Date.now(),
    };
    setLoc(next);
    writeCache(next);
    setPerm('granted');
    return next;
  }, [loc]);

  // GPS — current location (high accuracy)
  const requestGps = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported on this device.');
      return null;
    }
    setReq(true);
    setError(null);
    try {
      const pos = await getGpsPosition({ highAccuracy: true, timeout: 15000, maxAge: 0 });
      const next = await applyGpsCoords(pos.lat, pos.lng, pos.accuracy);
      setReq(false);
      return next;
    } catch (err) {
      setReq(false);
      const code = err?.code;
      if (code === 1) {
        setPerm('denied');
        setError('Location permission denied. Pick your city manually from the location chip.');
      } else if (code === 2) {
        setError('Location unavailable. Turn on GPS or pick a city manually.');
      } else if (code === 3) {
        setError('Location timed out. Try again or pick a city manually.');
      } else {
        setError('Could not get location. Pick a city manually.');
      }
      return null;
    }
  }, [applyGpsCoords]);

  // AUTO: request current location on first load (browser shows Allow/Deny).
  // Skips only if we already have fresh GPS, or user denied permission.
  useEffect(() => {
    if (autoGpsRef.current) return;
    if (permission === 'denied') return;
    if (loc?.source === 'gps') return;

    autoGpsRef.current = true;
    const delay = loc?.source === 'ip' ? 600 : 1200;
    const t = setTimeout(() => { requestGps(); }, delay);
    return () => clearTimeout(t);
  }, [permission, loc?.source, requestGps]);

  // Manual city pick
  const setManualCity = useCallback(async (city, opts = {}) => {
    let coords = opts.coords || coordsForCity(city) || null;
    if (!coords) {
      const fwd = await forwardGeocode(city);
      if (fwd) coords = fwd;
    }
    if (!coords) coords = loc?.coords || null;

    const next = {
      coords,
      city,
      area: opts.area || '',
      source: 'manual',
      accuracy: coords ? 15000 : 0,
      detectedAt: Date.now(),
    };
    setLoc(next);
    writeCache(next);
    const recents = readRecents().filter(c => c.toLowerCase() !== city.toLowerCase());
    recents.unshift(city);
    writeRecents(recents);
  }, [loc]);

  const clearLocation = useCallback(() => {
    setLoc(null);
    try { localStorage.removeItem(LS_KEY); } catch {}
  }, []);

  const distanceTo = useCallback((lat2, lng2) => {
    if (!loc?.coords) return null;
    return distanceKm(loc.coords.lat, loc.coords.lng, lat2, lng2);
  }, [loc]);

  const sortByDistance = useCallback(list => {
    if (!loc?.coords || !Array.isArray(list)) return list || [];
    return [...list].sort((a, b) => {
      const dA = distanceTo(Number(a.latitude ?? a.lat), Number(a.longitude ?? a.lng)) ?? Infinity;
      const dB = distanceTo(Number(b.latitude ?? b.lat), Number(b.longitude ?? b.lng)) ?? Infinity;
      return dA - dB;
    });
  }, [loc, distanceTo]);

  const value = {
    loc,
    coords: loc?.coords || null,
    city: loc?.city || '',
    area: loc?.area || '',
    source: loc?.source || null,
    accuracy: loc?.accuracy ?? null,
    requesting,
    error,
    permission,
    requestGps,
    setManualCity,
    clearLocation,
    distanceTo,
    sortByDistance,
    recents: readRecents(),
    hasLocationService: true,
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation must be used inside <LocationProvider>');
  return ctx;
}

export default useLocation;
